import { useState } from "react";

// Small inline "Copy" control for AI-generated drafts (case summary,
// suggested action, volume summary, chat replies). Resolves the text
// at click time so callers can pass either a string or a thunk that
// reads fresh state. Falls back to window.prompt if the Clipboard API
// is unavailable (e.g. older browsers, non-secure contexts).
interface Props {
  // String, or a thunk that returns the current text. Use the thunk
  // form when the source content can change after render.
  text: string | (() => string);
  // Override the default "Copy" / "✓ Copied" labels.
  label?: string;
  copiedLabel?: string;
  // Visual variant. "subtle" matches the existing btn-subtle pill;
  // "ghost" is borderless for tight inline placements (chat bubble
  // header, suggested-action header).
  variant?: "subtle" | "ghost";
  className?: string;
  title?: string;
  ariaLabel?: string;
}

export default function CopyButton({
  text,
  label = "Copy",
  copiedLabel = "✓ Copied",
  variant = "subtle",
  className,
  title,
  ariaLabel,
}: Props) {
  const [copied, setCopied] = useState(false);

  async function handleClick() {
    const value = typeof text === "function" ? text() : text;
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt("Copy this text:", value);
    }
  }

  const baseClass = variant === "ghost" ? "copy-btn copy-btn-ghost" : "copy-btn btn-subtle";
  const cls = className ? `${baseClass} ${className}` : baseClass;

  return (
    <button
      type="button"
      className={cls}
      onClick={handleClick}
      title={title ?? "Copy to clipboard"}
      aria-label={ariaLabel ?? title ?? "Copy to clipboard"}
    >
      {copied ? copiedLabel : label}
    </button>
  );
}
